import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  Brain,
  Heart,
  Globe,
  Lightbulb,
  Award,
  ArrowRight,
  Sparkles,
  Users,
  Leaf,
  Palette,
  Calculator
} from "lucide-react";
import LandingNavigation from "@/components/LandingNavigation";
import Footer from "@/components/ui/footer";
import { useSEO } from "@/hooks/useSEO";

const educationalTopics = [
  {
    title: "Kindness & Empathy",
    description: "Stories that teach children about being kind and understanding others' feelings",
    icon: Heart,
    color: "from-pink-400 to-rose-400"
  },
  {
    title: "Science & Nature",
    description: "Explore the wonders of the natural world through engaging narratives",
    icon: Leaf,
    color: "from-green-400 to-emerald-400"
  },
  {
    title: "Problem Solving",
    description: "Stories where characters think creatively to overcome challenges",
    icon: Lightbulb,
    color: "from-yellow-400 to-orange-400"
  },
  {
    title: "Cultural Awareness",
    description: "Learn about different cultures and traditions around the world",
    icon: Globe,
    color: "from-blue-400 to-cyan-400"
  },
  {
    title: "Creativity & Arts",
    description: "Inspire imagination and artistic expression",
    icon: Palette,
    color: "from-purple-400 to-pink-400"
  },
  {
    title: "Numbers & Logic",
    description: "Gentle introduction to mathematical concepts through stories",
    icon: Calculator,
    color: "from-indigo-400 to-blue-400"
  }
];

const learningBenefits = [
  {
    icon: Brain,
    title: "Cognitive Development",
    description: "Stories enhance memory, comprehension, and critical thinking skills"
  },
  {
    icon: Heart,
    title: "Emotional Intelligence",
    description: "Learn to identify and understand emotions through character experiences"
  },
  {
    icon: Users,
    title: "Social Skills",
    description: "Stories model positive interactions and relationship building"
  },
  {
    icon: Award,
    title: "Value Formation",
    description: "Develop character traits like honesty, courage, and perseverance"
  }
];

const sampleStories = [
  {
    title: "The Sharing Squirrel",
    lesson: "Sharing & Generosity",
    description: "Sammy learns the joy of sharing with friends",
    ageRange: "3-5"
  },
  {
    title: "Maya's Moon Mission",
    lesson: "Science & Curiosity",
    description: "A curious girl explores space and learns about the solar system",
    ageRange: "5-7"
  },
  {
    title: "The Friendship Forest",
    lesson: "Diversity & Inclusion",
    description: "Different animals discover that differences make friendships special",
    ageRange: "4-6"
  },
  {
    title: "Benny's Big Feelings",
    lesson: "Emotional Regulation",
    description: "A bear learns healthy ways to express and manage emotions",
    ageRange: "3-6"
  }
];

export default function EducationalStoriesPage() {
  useSEO({
    title: 'Educational Stories for Children | Learning Through Stories | SnuggleCrafters',
    description: 'Educational AI stories that teach while entertaining. Help your child learn values, science, and life lessons through engaging personalized tales.',
    keywords: 'educational stories, learning stories, teaching stories, children education, story-based learning, values stories for kids',
    url: '/stories/educational',
    type: 'website',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Stories', url: '/stories' },
      { name: 'Educational Stories', url: '/stories/educational' }
    ]
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <LandingNavigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 mb-6">
            <BookOpen className="h-4 w-4" />
            <span className="text-sm font-medium">Learn while they sleep</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
            Educational Stories for Kids
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Stories that teach important lessons while entertaining. Help your child develop values,
            knowledge, and life skills through engaging, personalized tales.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/auth">
              <Button size="lg" className="gap-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700">
                <Sparkles className="h-5 w-5" />
                Create an Educational Story
              </Button>
            </Link>
            <Link href="/features">
              <Button size="lg" variant="outline" className="gap-2">
                Learn More
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Educational Topics */}
      <section className="py-16 px-4 bg-white/50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Educational Topics</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Choose from a variety of educational themes for your child's stories.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {educationalTopics.map((topic, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer">
                <CardContent className="pt-6">
                  <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${topic.color} flex items-center justify-center mb-4`}>
                    <topic.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{topic.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">{topic.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Benefits */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">How Stories Help Learning</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-8">
                Research shows that children learn best through stories. Narratives help children
                understand complex concepts, remember information longer, and apply lessons to their own lives.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {learningBenefits.map((benefit, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
                    <benefit.icon className="h-8 w-8 text-green-600 mb-3" />
                    <h3 className="font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-100 to-blue-100 dark:from-green-900/30 dark:to-blue-900/30 rounded-3xl p-8">
              <div className="text-center mb-6">
                <Brain className="h-16 w-16 mx-auto text-green-600 mb-4" />
                <h3 className="text-2xl font-bold">Sample Educational Stories</h3>
              </div>
              <div className="space-y-3">
                {sampleStories.map((story, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">{story.title}</h4>
                      <Badge variant="secondary" className="text-xs">{story.ageRange}</Badge>
                    </div>
                    <Badge className="mb-2 bg-green-100 text-green-700 hover:bg-green-100">
                      {story.lesson}
                    </Badge>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{story.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Story Types */}
      <section className="py-16 px-4 bg-white/50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">More Story Types</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Explore our other story categories for different bedtime needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link href="/stories/bedtime">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <span className="text-2xl">🌙</span> Calming Bedtime
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Gentle stories designed to help children wind down and fall asleep peacefully.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link href="/stories/age-3-5">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <span className="text-2xl">👶</span> Ages 3-5
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Simple, engaging stories perfect for toddlers and preschoolers.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link href="/stories/age-6-8">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <span className="text-2xl">📚</span> Ages 6-8
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Longer, more complex stories for growing readers with deeper themes.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Make Learning Fun Tonight</h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Create a personalized educational story that teaches while it entertains.
          </p>
          <Link href="/auth">
            <Button size="lg" className="gap-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700">
              <BookOpen className="h-5 w-5" />
              Start Creating Free
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
